import { Box, ListItemButton } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';

import { Label } from '@/modules/layers/components/common/Label';
import { useLayerStore } from '@/modules/layers/store/useLayerStore';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { LayerVisibilityMode } from '@/modules/common/types/layers';

import { LayerActions } from './LayerActions';
import { LayerServiceFactory } from '../service/LayerServiceFactory';

type Props = {
  label?: string;
  id: string;
  level: number;
  canBeLocked?: boolean;
  canBeHidden?: boolean;
  canBeSelected?: boolean;
  disabled?: boolean;
};

const AreaItemComponent = ({
  label,
  id,
  level,
  canBeLocked,
  canBeHidden = true,
  canBeSelected = true,
  disabled = false,
}: Props) => {
  const invisibleElements = useLayerStore((state) => state.invisibleElements);
  const lockedElements = useLayerStore((state) => state.lockedElements);

  const getTemplateByAreaId = useFloorplanDesignStore((state) => state.getTemplateByAreaId);
  const getGroupsByAreaIds = useFloorplanDesignStore((state) => state.getGroupsByAreaIds);

  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const canvasSvc = useMemo(() => CanvasServiceFactory.getInstance().createCanvasService(), []);
  const layerSvc = useMemo(() => LayerServiceFactory.getInstance().createLayerService(), []);

  const [hover, setHover] = useState(false);

  const elementIsVisible = useMemo(() => !invisibleElements.has(id), [id, invisibleElements]);

  const elementIsLocked = useMemo(() => lockedElements.has(id), [id, lockedElements]);

  const relatedGroups = useMemo(() => getGroupsByAreaIds([id]), [getGroupsByAreaIds, id]);

  // Ids that make this row show as selected: the area itself, its template and its groups.
  const selectingIds = useMemo(() => {
    const template = getTemplateByAreaId(id);

    return [
      id,
      ...(template ? [template.group.id] : []),
      ...relatedGroups.map((group) => group.id),
    ];
  }, [getTemplateByAreaId, id, relatedGroups]);

  // Boolean selectors: a row only re-renders when its own selected state changes,
  // instead of every row re-rendering on every selection change.
  const isDirectlySelected = useAppStore((state) => state.selectedIds.includes(id));
  const selected = useAppStore((state) =>
    selectingIds.some((selectingId) => state.selectedIds.includes(selectingId)),
  );

  const setElementVisibilityMode = useCallback(
    (visibilityMode: LayerVisibilityMode) => {
      if (visibilityMode === 'force_hide') {
        layerSvc.hideElements([id]);
        return;
      }

      layerSvc.showElements([id]);
    },
    [id, layerSvc],
  );

  const toggleLock = useCallback(() => {
    if (elementIsLocked) {
      layerSvc.unlockElements([id]);
      return;
    }

    layerSvc.lockElements([id]);
  }, [elementIsLocked, id, layerSvc]);

  const handleSelect = useCallback(() => {
    if (disabled || !elementIsVisible || elementIsLocked) return;

    if (canBeSelected) appSvc.setSelectedIds([id]);
    canvasSvc.zoomToFitElements([id]);
  }, [appSvc, canBeSelected, canvasSvc, disabled, elementIsLocked, elementIsVisible, id]);

  const stopRowClick = useCallback((event: MouseEvent) => {
    event.stopPropagation();
  }, []);

  const startHover = useCallback(() => {
    setHover(true);
  }, []);

  const endHover = useCallback(() => {
    setHover(false);
  }, []);

  useEffect(() => {
    if (!isDirectlySelected) return;
    if (elementIsVisible && !elementIsLocked) return;

    appSvc.removeSelectedIds([id]);
  }, [appSvc, elementIsLocked, elementIsVisible, id, isDirectlySelected]);

  const containerStyle = useMemo(
    () => ({
      paddingLeft: 4 + 2 * level,
      color: !elementIsVisible ? 'neutral.main' : 'neutral.darkest',
      backgroundColor: selected ? 'neutral.grey' : 'shades.light',
      borderColor: selected ? 'neutral.grey' : 'shades.light',

      '&:hover': {
        backgroundColor: selected ? 'neutral.grey' : 'shades.light',
      },
    }),
    [elementIsVisible, level, selected],
  );

  const visibilityMode: LayerVisibilityMode = elementIsVisible ? 'force_show' : 'force_hide';
  const showActions =
    (canBeLocked || canBeHidden) && (hover || elementIsLocked || !elementIsVisible);

  return (
    <ListItemButton
      alignItems='center'
      onClick={handleSelect}
      onMouseOver={startHover}
      onMouseLeave={endHover}
      sx={containerStyle}
    >
      <Label labelText={label ?? id} />

      {showActions && (
        <Box component='span' onClick={stopRowClick} sx={{ display: 'inline-flex' }}>
          <LayerActions
            isLocked={elementIsLocked}
            visibilityMode={visibilityMode}
            showAuto={false}
            showLock={canBeLocked}
            showVisibility={canBeHidden}
            disabled={disabled}
            onToggleLock={toggleLock}
            onVisibilityModeChange={setElementVisibilityMode}
          />
        </Box>
      )}
    </ListItemButton>
  );
};

export const AreaItem = memo(AreaItemComponent);
